import { OPS_ENDPOINTS } from "@constants/endpoints";
import useDocumentViewer from "@panel/shared/contract/hooks/use-document-viewer";
import usePublisherContracts from "@panel/shared/contract/hooks/use-publisher-contracts";
import useBrandTemplate from "./use-brand-template";
import useSendContracts from "./use-send-contracts";
import useTemplateUpload from "./use-template-upload";

/**
 * The brand's Contract tab: one agreement uploaded against the brand, then
 * sent to its publishers — all at once or one by one. Each send creates that
 * publisher's contract on a queue, so the table catches up rather than
 * flipping instantly.
 *
 * The template and each publisher's contract are previewed in-page through
 * their /document routes.
 * @param brandId
 * @param brandName
 */
const useTripartiteAgreement = (brandId, brandName) => {
    const {
        publishers, counts, pagination, loading: publishersLoading,
        handlePageChange, handlePerRowsChange, refetch: refetchPublishers
    } = usePublisherContracts({
        base: "ops",
        path: brandId ? OPS_ENDPOINTS.CONTRACT_PUBLISHERS(brandId) : ""
    });

    const { loading: templateLoading, saving, template, saveTemplate } = useBrandTemplate(brandId);
    const hasTemplate = Boolean(template);

    const upload = useTemplateUpload({
        brandName,
        hasTemplate,
        saving,
        saveTemplate,
        onSaved: refetchPublishers
    });

    const send = useSendContracts(brandId, refetchPublishers);
    const { openPreview, modal: previewModal } = useDocumentViewer("ops");

    return {
        template,
        hasTemplate,
        templateLoading,
        uploading      : upload.uploading,
        openPicker     : upload.openPicker,
        inputProps     : upload.inputProps,
        confirmModal   : upload.confirmModal,
        onViewTemplate : () => openPreview(OPS_ENDPOINTS.BRAND_CONTRACT_FILE(brandId), template?.name, brandName),
        publishers,
        counts,
        pagination,
        publishersLoading,
        handlePageChange,
        handlePerRowsChange,
        onRefresh      : refetchPublishers,
        canSendAll     : hasTemplate && counts.notSent > 0,
        sendingAll     : send.sendingAll,
        onSendAll      : send.handleSendAll,
        isSending      : send.isSending,
        onSendOne      : send.handleSendOne,
        // Only offered while something is actually queued; the API only
        // re-queues what has been stuck for over 10 minutes.
        canRetry       : counts.queued + counts.brandSignQueued > 0,
        retrying       : send.retrying,
        onRetry        : send.handleRetry,
        onViewPublisher: (row) => openPreview(
            OPS_ENDPOINTS.CONTRACT_DOCUMENT(brandId, row.contractId),
            `${template?.name || "contract"} - ${row.name}`,
            row.name
        ),
        previewModal
    };
};

export default useTripartiteAgreement;
